.blockDocument {
  line-height: 1.7;
  font-size: 0.95rem;
  color: var(--main-text);
}

/* ===== Dyad filter bars ===== */
.filterBar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 0 8px 0;
  position: sticky;
  top: 0;
  background: var(--main-background, white);
  z-index: 10;
  align-items: center;
}

.countryFilterBar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 0 12px 0;
  align-items: center;
  border-bottom: 1px solid var(--box-border-color, #e5e7eb);
  margin-bottom: 4px;
}

.filterLabel {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--secondary-text, #9ca3af);
  margin-right: 2px;
  min-width: 52px;
}

.filterPill {
  background: var(--main-background, white);
  border: 1.5px solid var(--box-border-color, #d1d5db);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--main-text, #374151);
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.filterPill:hover:not(.filterPillDisabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.filterPillActive {
  border-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.filterClear {
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--secondary-text, #9ca3af);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.filterClear:hover {
  color: var(--main-text, #374151);
}

/* Section blocks — divider + heading */
.section {
  margin: 32px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--box-border-color, #e0e0e0);
}

.section:first-child {
  margin-top: 0;
}

.sectionTitle {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--main-text);
  margin: 0;
}

.sectionCaption {
  font-size: 0.8rem;
  color: var(--secondary-text, #888);
  font-style: italic;
}

/* Title row — title + caption on the same line */
.titleRow {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 0 0 6px 0;
}

.titleRow h3 {
  margin: 0;
}

.blockTitle {
  font-size: 1rem;
  font-weight: 600;
  color: var(--main-text);
  margin: 0 0 6px 0;
}

.caption {
  font-size: 0.8rem;
  color: var(--secondary-text, #888);
  font-style: italic;
  white-space: nowrap;
}

/* ===== Dyad title row ===== */
.titleRowDyad {
  padding: 0 0 8px 0;
  margin: 0;
}

.titleRowDyad h3 {
  margin: 0;
}

.dyadTitleLine {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.dyadBlockTitle {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--secondary-text, #6b7280);
  margin: 0;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.dyadHeadline {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--main-text, #1a1a1a);
  line-height: 1.4;
  margin: 4px 0 0 0;
  width: 100%;
}

/* Caption badges (dyad reports) */
.captionBadges {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.badge {
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* Trajectory badge (next to status badge) */
.trajectoryBadge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 500;
  color: var(--secondary-text, #6b7280);
  white-space: nowrap;
}

.trajectoryIcon {
  font-size: 14px !important;
  vertical-align: middle;
  position: relative;
  top: -1px;
}

/* Text blocks */
.textBlock {
  margin: 0 0 20px 0;
}

.textContent {
  font-size: 0.95rem;
  color: var(--main-text);
}

.textContent p {
  margin: 0 0 8px 0;
}

.textContent p:last-of-type {
  display: inline;
  margin-bottom: 0;
}

.textContent ul,
.textContent ol {
  margin: 0 0 8px 0;
  padding-left: 24px;
}

.textContent li {
  margin-bottom: 4px;
}

.textContent h1,
.textContent h2,
.textContent h3,
.textContent h4 {
  margin: 16px 0 8px 0;
  color: var(--main-text);
}

.textContent blockquote {
  border-left: 3px solid var(--main-blue, #4c90f0);
  margin: 8px 0;
  padding: 4px 12px;
  color: var(--secondary-text, #555);
}

/* ===== Dyad text block variant ===== */
.textBlockDyad {
  margin: 0;
  padding: 16px 0;
  border-bottom: 1px solid var(--box-border-color, #e5e7eb);
  border-radius: 0;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.textBlockDyad:last-child {
  border-bottom: none;
}

/* When expanded, wrap the entire dyad block as a distinct card */
.textBlockDyadExpanded {
  background: var(--light-grey, #f8f9fa);
  border: 1px solid var(--box-border-color, #e0e0e0);
  border-radius: 10px;
  padding: 16px 18px;
  margin: 8px 0;
}

.textContentDyad {
  font-size: 0.9rem;
  color: var(--body-text, #444);
  line-height: 1.65;
}

/* ===== Dyad collapse / expand ===== */

.dyadShortSummary {
  margin: 0;
  font-size: 0.88rem;
  color: var(--body-text, #444);
  line-height: 1.6;
  transition:
    opacity 0.3s ease,
    font-style 0s,
    font-size 0.3s ease,
    letter-spacing 0.3s ease,
    border-left-color 0.3s ease,
    padding-left 0.3s ease;
  border-left: 2px solid transparent;
  padding-left: 0;
}

.dyadShortSummaryDimmed {
  opacity: 0.65;
  font-style: italic;
  font-size: 0.82rem;
  letter-spacing: 0.01em;
  border-left-color: var(--box-border-color, #d1d5db);
  padding-left: 10px;
}

/* Grid-row trick: 0fr → 1fr animates height without JS measurement */
.dyadDetailWrapper {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease;
}

.dyadDetailOpen {
  grid-template-rows: 1fr;
}

.dyadDetailInner {
  overflow: hidden;
}

.dyadDetailOpen .dyadDetailInner {
  padding-top: 8px;
}

/* Modern expand/collapse button */
.dyadExpandBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--main-background, #fff);
  border: 1px solid var(--box-border-color, #e5e7eb);
  border-radius: 6px;
  padding: 5px 12px;
  margin-top: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--secondary-text, #6b7280);
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.01em;
  font-family: inherit;
}

.dyadExpandBtn:hover {
  color: var(--main-text, #374151);
  border-color: var(--secondary-text, #9ca3af);
  background: var(--light-grey, #f9fafb);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.dyadExpandBtnActive {
  color: var(--main-text, #374151);
  background: var(--light-grey, #f3f4f6);
  border-color: var(--secondary-text, #9ca3af);
}

.dyadExpandBtnIcon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  transition: transform 0.18s ease;
}

/* Segments — inline source attribution (dyad reports) */
.segment {
  display: inline;
}

/* Sources — inline pills after text */
.sources {
  display: inline;
  margin-left: 4px;
}

.sourceLoading {
  color: var(--secondary-text);
  font-size: 0.8rem;
}

.sourceMissing {
  display: inline;
  font-size: 0.75rem;
  color: var(--secondary-text);
  background: var(--light-grey, #f6f7f9);
  padding: 2px 6px;
  border-radius: 10px;
  margin: 0 2px;
}

/* Table blocks */
.tableSection {
  margin: 20px 0;
}

.tableWrapper {
  overflow-x: auto;
  border: 1px solid var(--box-border-color, #e0e0e0);
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9375rem;
  background: var(--main-background, white);
}

.thead {
  background: var(--light-grey, #f6f7f9);
  border-bottom: 2px solid var(--main-text, #333);
}

.th {
  text-align: left;
  padding: 10px 12px;
  font-weight: 600;
  color: var(--main-text);
  border-right: 1px solid var(--box-border-color, #e0e0e0);
}

.th:last-child {
  border-right: none;
}

.table tbody tr:nth-child(even) {
  background: var(--light-grey, #f6f7f9);
}

.tr {
  border-bottom: 1px solid var(--box-border-color, #e8e8e8);
}

.tr:last-child {
  border-bottom: none;
}

.td {
  padding: 10px 12px;
  vertical-align: top;
  color: var(--body-text, #555);
  border-right: 1px solid var(--box-border-color, #e8e8e8);
}

.td:last-child {
  border-right: none;
}

.tdNumeric {
  text-align: right;
  font-family: "IBM Plex Mono", monospace;
}

.tableSources {
  margin-top: 8px;
}

/* =========================================================================
   TOC block
   ========================================================================= */

.toc {
  float: right;
  clear: right;
  margin: 0 0 10px 24px;
  max-width: 320px;
  background: var(--main-background, white);
  padding: 12px 12px 12px 16px;
  border: 1px solid var(--box-border-color, #e0e0e0);
}

.tocPinned {
  position: sticky;
  top: 64px;
  z-index: 10;
}

.tocScrolled {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Header row: title + action buttons */
.tocHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.tocToggle {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}

.tocTitle {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--main-blue, #4c90f0);
}

.tocToggle:hover .tocTitle {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.tocPinBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  margin-left: auto;
  cursor: pointer;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.tocPinBtn svg {
  width: 14px;
  height: 14px;
  fill: rgba(76, 144, 240, 0.4);
  transition: fill 0.2s ease;
}

.tocPinBtn:hover svg {
  fill: rgba(76, 144, 240, 0.7);
}

.tocPinBtnActive {
  transform: rotate(-45deg);
}

.tocPinBtnActive svg {
  fill: var(--main-blue, #4c90f0);
}

/* Entry list with smooth collapse */
.tocList {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  max-height: 60vh;
  transition:
    max-height 0.3s ease,
    opacity 0.25s ease;
  opacity: 1;
}

.tocListCollapsed {
  max-height: 0;
  opacity: 0;
}

.tocEntry {
  padding: 5px 0;
}

.tocHeadingRow {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.tocHeading {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--main-text, #333);
  cursor: pointer;
  text-align: left;
  transition: color 0.1s;
}

.tocHeading:hover {
  color: var(--main-blue, #4c90f0);
}

.tocHeadingInert {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--main-text, #333);
}

.tocSubList {
  list-style: none;
  margin: 3px 0 0 0;
  padding: 0 0 0 12px;
}

.tocSubEntry {
  padding: 2px 0;
}

.tocSubLink {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 400;
  color: var(--secondary-text, #666);
  cursor: pointer;
  text-align: left;
  transition: color 0.1s;
}

.tocSubLink:hover {
  color: var(--main-blue, #4c90f0);
}

.tocSubInert {
  font-size: 0.82rem;
  color: var(--secondary-text, #666);
}

/* ---- Mobile: stack TOC ---- */
@media (max-width: 768px) {
  .toc {
    float: none;
    max-width: none;
    margin: 0 0 24px 0;
  }

  .toc.tocPinned {
    position: static;
    box-shadow: none;
  }

  .tocPinBtn {
    display: none;
  }
}

/* ---- Print ---- */
@media print {
  .tocNoPrint {
    display: none;
  }

  .toc {
    float: none;
    max-width: none;
    margin: 0 0 20px 0;
  }

  .toc.tocPinned {
    position: static;
    box-shadow: none;
  }

  .tocPinBtn {
    display: none;
  }

  .tocChevron {
    display: none;
  }

  .tocListCollapsed {
    max-height: none;
    opacity: 1;
  }
}
